import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import type {RecruitmentsType} from '../../core/entities/recruitment/schema';
import {getRecruitmentList} from '../../core/repositories/recruitingInfoRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../core/repositories/usersMeRepository';
import {validateCookie} from '../../core/usecases/auth/checkOauthTokenExistence';
import {RecruitingInfoForPc} from '../../features/recruitingInfo/components/Container/RecruitingInfoForPC';
import {RecruitingInfoForSP} from '../../features/recruitingInfo/components/Container/RecruitingInfoForSP';
import {withHandler} from '../../server/token/withHandler';
import {ScriptForGA} from '../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../shared/components/layouts/NoLeftSidebarLayout';
import {useMediaQuery} from '../../shared/services/useMediaQuery';
import type {NextPageWithLayout} from '../_app';

type Props = {
  defaultRecruitments?: RecruitmentsType;
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
};

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession'
>(
  {auth: 'optional'},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const props: Props = {};
    const {cookies} = context.req;
    const response = await getRecruitmentList({
      cookies,
      queryParams: {
        page: 1,
        limit: 30,
        order: 'recommend',
        industryCategoryIDs: [],
        tagIDs: [],
        typeIDs: [],
        groupedJobTypeIDs: [],
      },
    });

    const {isValid} = validateCookie(cookies);
    if (!isValid) {
      return {
        props: {
          defaultRecruitments: response.ok ? response.value.recruitments : [],
        },
      };
    }

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props: {
          defaultRecruitments: response.ok ? response.value.recruitments : [],
        },
      };
    }

    return {
      props: {
        ...props,
        defaultRecruitments: response.ok ? response.value.recruitments : [],
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  defaultRecruitments,
  userDataForAnalytics,
}: Props) => {
  const {isPC} = useMediaQuery();
  return (
    <>
      {isPC ? (
        <RecruitingInfoForPc />
      ) : (
        <RecruitingInfoForSP defaultRecruitments={defaultRecruitments} />
      )}
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;

export default Page;
